
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ChevronLeft, 
  Play, 
  FileText, 
  MousePointer2, 
  Award, 
  Clock, 
  CheckCircle2, 
  ArrowRight,
  Brain,
  Target,
  Info,
  Layers
} from 'lucide-react';
import { cn, logInteraction } from '../lib/utils';
import { ACADEMY_DATA, Lesson, Course } from '../data/academyData';

export default function AcademyLessonPage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleNextModule = () => {
    logInteraction('AcademyLesson', 'Next Module', { lesson: lesson?.title });
    if (!lesson || !course || !quizCompleted) return;
    
    const currentIndex = course.lessons.findIndex(l => l.id === lesson.id);
    if (currentIndex < course.lessons.length - 1) {
      const nextLesson = course.lessons[currentIndex + 1];
      navigate(`/academy/module/${nextLesson.id}`);
      // Scroll to top
      window.scrollTo(0, 0);
    } else {
      setNotification({ message: "Course completed! Returning to Academy.", type: 'success' });
      setTimeout(() => navigate('/academy'), 1500);
    }
  };

  const handleResourceDownload = (name: string) => {
    logInteraction('AcademyLesson', 'Download Resource', { resource: name });
    // In a real app, these would be valid asset paths
    // For this demo, we'll simulate a successful "open" or a "file missing" error
    const isAvailable = Math.random() > 0.1; // 90% success rate for demo

    if (isAvailable) {
      setNotification({ message: `Opening ${name}...`, type: 'success' });
      // Simulate opening a PDF
      setTimeout(() => {
        window.open('https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', '_blank');
      }, 500);
    } else {
      setNotification({ message: `Error: ${name} source file not found on server.`, type: 'error' });
    }
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      let foundLesson: Lesson | null = null;
      let foundCourse: Course | null = null;

      for (const track of ACADEMY_DATA) {
        for (const c of track.courses) {
          const l = c.lessons.find(l => l.id === moduleId);
          if (l) {
            foundLesson = l;
            foundCourse = c;
            break;
          }
        }
        if (foundLesson) break;
      }

      setLesson(foundLesson);
      setCourse(foundCourse);
      
      // If the lesson is not a quiz, mark it as completed so the user can navigate
      if (foundLesson && foundLesson.type !== 'quiz') {
        setQuizCompleted(true);
      } else {
        setQuizCompleted(false);
      }
      
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [moduleId]);

  if (loading) {
    return (
      <div className="app-container py-12 space-y-8 animate-pulse">
        <div className="h-8 w-48 bg-[var(--input-bg)] rounded-lg" />
        <div className="h-64 w-full bg-[var(--input-bg)] rounded-3xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 h-96 bg-[var(--input-bg)] rounded-3xl" />
          <div className="h-96 bg-[var(--input-bg)] rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!lesson || !course) {
    return (
      <div className="app-container py-20 text-center space-y-6">
        <div className="w-20 h-20 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto text-rose-600">
          <Info size={40} />
        </div>
        <h2 className="text-2xl font-black text-[var(--app-text)] uppercase italic">Module Not Found</h2>
        <p className="text-[var(--muted-text)] max-w-md mx-auto">The requested learning module could not be located in our institutional database.</p>
        <Link 
          to="/academy"
          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-black uppercase italic tracking-widest hover:bg-emerald-500 transition-all"
        >
          <ChevronLeft size={18} /> Back to Academy
        </Link>
      </div>
    );
  }

  return (
    <div className="app-container py-8 space-y-8 pb-24 relative">
      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div 
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 20, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className={cn(
              "fixed top-4 left-1/2 z-50 px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 min-w-[300px]",
              notification.type === 'success' ? "bg-emerald-600 border-emerald-500 text-white" : "bg-rose-600 border-rose-500 text-white"
            )}
          >
            {notification.type === 'success' ? <CheckCircle2 size={18} /> : <Info size={18} />}
            <span className="text-xs font-black uppercase italic tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Module Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-2">
          <button 
            onClick={() => {
              logInteraction('AcademyLesson', 'Back to Course', { course: course.title });
              navigate(-1);
            }}
            className="flex items-center gap-2 text-[10px] font-black text-[var(--muted-text)] hover:text-emerald-600 transition-colors uppercase tracking-widest italic mb-2"
          >
            <ChevronLeft size={14} /> Back to {course.title}
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600/10 flex items-center justify-center text-emerald-600">
              {lesson.type === 'video' && <Play size={20} className="fill-emerald-600" />}
              {lesson.type === 'reading' && <FileText size={20} />}
              {lesson.type === 'interactive' && <MousePointer2 size={20} />}
              {lesson.type === 'quiz' && <Award size={20} />}
            </div>
            <h1 className="text-3xl font-black text-[var(--app-text)] uppercase italic tracking-tight">{lesson.title}</h1>
          </div>
          <div className="flex items-center gap-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest ml-1">
            <span className="flex items-center gap-1"><Clock size={12} /> {lesson.duration}</span>
            <span className="w-1 h-1 bg-[var(--card-border)] rounded-full" />
            <span>{course.level}</span>
            <span className="w-1 h-1 bg-[var(--card-border)] rounded-full" />
            <span className="text-emerald-600">{lesson.type}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right hidden md:block">
            <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Course Progress</div>
            <div className="text-lg font-black text-emerald-600 italic">66%</div>
          </div>
          <div className="w-32 h-2 bg-[var(--input-bg)] rounded-full overflow-hidden hidden md:block">
            <div className="h-full bg-emerald-600 w-[66%]" />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Lesson Content Area */}
        <div className="lg:col-span-8 space-y-8">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2.5rem] overflow-hidden shadow-xl">
            {/* Video or Lesson Content */}
            {lesson.type === 'video' ? (
              <div 
                className="aspect-video bg-slate-900 relative flex items-center justify-center group cursor-pointer"
                onClick={() => {
                  logInteraction('AcademyLesson', 'Play Video', { lesson: lesson.title });
                  setNotification({ message: "Starting video lecture...", type: 'success' });
                }}
              >
                <img 
                  src={`https://picsum.photos/seed/${lesson.id}/1280/720`} 
                  alt={lesson.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-30 transition-opacity"
                  referrerPolicy="no-referrer"
                />
                <div className="relative z-10 flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-emerald-600 flex items-center justify-center text-white shadow-2xl shadow-emerald-600/40 group-hover:scale-110 transition-transform">
                    <Play size={32} className="fill-white ml-1" />
                  </div>
                  <span className="text-xs font-black text-white uppercase tracking-[0.2em] italic">Click to Play Lecture</span>
                </div>
              </div>
            ) : (
              <div className="p-10 space-y-8">
                <div className="prose prose-invert max-w-none">
                  <p className="text-lg text-[var(--app-text)] font-medium leading-relaxed italic">
                    In this module, we explore the mechanics of institutional order execution and how professional traders manage large positions without disrupting market equilibrium.
                  </p>
                  <div className="h-px bg-gradient-to-r from-emerald-500/50 to-transparent my-8" />
                  <div className="space-y-6 text-[var(--muted-text)] leading-relaxed">
                    <p>
                      Institutional traders rarely use simple market orders for large blocks of shares. Instead, they utilize sophisticated algorithms to slice orders into smaller, manageable pieces. This process, known as "shredding," helps minimize market impact and slippage.
                    </p>
                    <p>
                      One of the most common techniques is the <strong>Iceberg Order</strong>. An iceberg order is a large single order that has been divided into smaller limit orders, usually by the use of an automated program, for the purpose of hiding the actual order quantity.
                    </p>
                    <div className="bg-[var(--input-bg)] p-6 rounded-2xl border border-[var(--input-border)]">
                      <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic mb-3 flex items-center gap-2">
                        <Target size={18} className="text-emerald-600" /> Key Takeaway
                      </h4>
                      <p className="text-xs font-medium italic">
                        The "visible" portion of an iceberg order is just the tip. Market participants only see a small fraction of the total liquidity available at that price level, which can lead to unexpected support or resistance.
                      </p>
                    </div>
                    <p>
                      Understanding these mechanics allows retail traders to identify institutional footprints on the Level 2 tape and align their strategies with the "smart money" flow.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Quiz Section (if applicable) */}
            <div className="p-10 bg-[var(--card-header-bg)] border-t border-[var(--card-header-border)]">
              {!quizStarted ? (
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-600">
                      <Brain size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic">Knowledge Check</h3>
                      <p className="text-xs font-bold text-[var(--muted-text)] uppercase tracking-widest">3 Questions • 50 XP Reward</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      logInteraction('AcademyLesson', 'Start Quiz', { lesson: lesson.title });
                      setQuizStarted(true);
                    }}
                    className="px-8 py-3 bg-slate-900 text-white rounded-xl text-sm font-black uppercase italic tracking-widest hover:bg-slate-800 transition-all shadow-lg"
                  >
                    Start Quiz
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic">Question 1 of 3</h3>
                    <div className="text-xs font-black text-emerald-600 uppercase italic">Time: 0:45</div>
                  </div>
                  <p className="text-base font-bold text-[var(--app-text)]">What is the primary purpose of using an Iceberg Order?</p>
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      "To execute orders as fast as possible regardless of price.",
                      "To hide the true size of a large order from other market participants.",
                      "To automatically buy at the bid and sell at the ask.",
                      "To guarantee execution at the next day's opening price."
                    ].map((opt, i) => (
                      <button 
                        key={i}
                        onClick={() => {
                          logInteraction('AcademyLesson', 'Submit Quiz Answer', { question: 1, answer: opt });
                          setQuizCompleted(true);
                          setNotification({ message: "Correct! Module completed.", type: 'success' });
                        }}
                        className="w-full p-4 text-left bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl hover:border-emerald-500 hover:bg-emerald-500/5 transition-all text-sm font-medium"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Next Module Button */}
          <div className="flex justify-end">
            <button 
              onClick={handleNextModule}
              disabled={!quizCompleted}
              className={cn(
                "group flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-black uppercase italic tracking-widest transition-all shadow-2xl",
                quizCompleted ? "bg-emerald-600 text-white shadow-emerald-600/20 hover:bg-emerald-500 active:scale-95" : "bg-slate-200 text-slate-400 cursor-not-allowed"
              )}
            >
              {course.lessons.findIndex(l => l.id === lesson.id) === course.lessons.length - 1 ? "Finish Course" : "Next Module"}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Sidebar: Key Concepts & Resources */}
        <div className="lg:col-span-4 space-y-8">
          {/* Key Concepts */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-widest italic mb-6 flex items-center gap-2">
              <Layers size={18} className="text-emerald-600" />
              Key Concepts
            </h3>
            <div className="space-y-4">
              {[
                { title: "Market Impact", desc: "The effect that a buy or sell order has on the price of an asset." },
                { title: "Slippage", desc: "The difference between the expected price and the executed price." },
                { title: "Dark Pools", desc: "Private exchanges for trading securities that are not accessible to the public." },
                { title: "Order Shredding", desc: "Breaking large orders into smaller pieces to avoid detection." }
              ].map((concept, i) => (
                <div key={i} className="p-4 bg-[var(--input-bg)] rounded-2xl border border-[var(--input-border)]">
                  <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic mb-1">{concept.title}</h4>
                  <p className="text-[10px] text-[var(--muted-text)] font-medium leading-relaxed">{concept.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Resources */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-3xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-widest italic mb-6 flex items-center gap-2">
              <FileText size={18} className="text-emerald-600" />
              Resources
            </h3>
            <div className="space-y-3">
              <button 
                onClick={() => handleResourceDownload('Lecture Slides')}
                className="w-full flex items-center justify-between p-3 bg-[var(--input-bg)] hover:bg-[var(--card-border)] rounded-xl transition-all group"
              >
                <span className="text-[10px] font-black text-[var(--app-text)] uppercase italic group-hover:text-emerald-600">Lecture Slides (PDF)</span>
                <FileText size={14} className="text-emerald-600" />
              </button>
              <button 
                onClick={() => handleResourceDownload('Execution Checklist')}
                className="w-full flex items-center justify-between p-3 bg-[var(--input-bg)] hover:bg-[var(--card-border)] rounded-xl transition-all group"
              >
                <span className="text-[10px] font-black text-[var(--app-text)] uppercase italic group-hover:text-emerald-600">Execution Checklist</span>
                <CheckCircle2 size={14} className="text-emerald-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
